import React, { useEffect, useRef, useState } from 'react';
import TagManager from 'react-gtm-module';
import { Helmet as HelmetBase } from 'react-helmet';
import { DocsVerificationWidget, DocsVerificationWidgetProps } from '@dataspike/docver-widget';
import axios from 'axios';
import { ethers } from 'ethers';

import { Spinner } from './components/spinner';
import { WalletScreen, WalletScreenVariant } from './components/wallet-screen';
import { useLogRocket } from './hooks/use-logrocket';
import { buildMetaMaskDeepLink, isMobileDevice, METAMASK_INSTALL_URL } from './utils/wallet';

import './app.css';
import './fonts.css';

const isDsHost = window.location.hostname.includes('am.dataspike.io');

const defaultAxios = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL || '',
});

export const App: React.FC = () => {
    const [isLoading, setIsLoading] = useState(true);
    const isInitialized = useRef(false);
    const isSettingsInitialized = useRef(false);
    const [settings, setSettings] = useState<{
        domain_settings: {
            api: string;
        };
    } | null>(null);
    const [domainPageTitle, setIsDomainPageTitle] = useState('');
    const [verificationId, setVerificationId] = useState('');
    const [zkProof, setZkProof] = useState(null);
    const [errorZkProof, setErrorZkProof] = useState('');
    const showZkProof = !!(zkProof || errorZkProof);
    const [apiUrl, setApiUrl] = useState(() => process.env.REACT_APP_API_BASE_URL);

    const [walletScreen, setWalletScreen] = useState<WalletScreenVariant | null>(null);
    const [isRequesting, setIsRequesting] = useState(false);

    const linkIdRef = useRef('');
    const apiUrlRef = useRef('');
    const searchRef = useRef<URLSearchParams | null>(null);

    const connectWallet = async (): Promise<string | null> => {
        if (!window.ethereum) {
            setWalletScreen('no-wallet');

            return null;
        }

        try {
            setIsRequesting(true);
            await window.ethereum.request({ method: 'eth_requestAccounts' });
            const metamaskProvider = new ethers.BrowserProvider(window.ethereum);
            const signer = await metamaskProvider.getSigner();

            return await signer.getAddress();
        } catch (e) {
            const code = (e as { code?: number })?.code;

            if (code === 4001) {
                setWalletScreen('access-denied');
            } else if (code === -32002) {
                setWalletScreen('pending');
            } else {
                // eslint-disable-next-line no-console
                console.error('Wallet connection failed', e);
                setErrorZkProof('Something went wrong. Please try again later');
            }

            return null;
        } finally {
            setIsRequesting(false);
        }
    };

    const proceedWithWallet = async (address: string) => {
        const linkId = linkIdRef.current;
        const apiBaseUrl = apiUrlRef.current;
        const params = new URLSearchParams(searchRef.current?.toString() ?? '');

        params.delete('web3');
        params.set('wallet', address);

        try {
            const zkProofData = await axios.post(`${apiBaseUrl}/api/v4/web3/zkproof/generate`, {
                wallet: address,
                link_id: linkId,
            });

            setZkProof(zkProofData.data.proof);
            isInitialized.current = true;
            setWalletScreen(null);

            return;
        } catch (e) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const err = e as any;

            if (err?.response?.data?.error_level === 'issuance_restricted') {
                setErrorZkProof(
                    'ZKProof has already been used for this wallet. Re-verification is not possible',
                );
                isInitialized.current = true;
                setWalletScreen(null);

                return;
            }
        }

        const verificationResponse = await axios.get(
            `${apiBaseUrl}/api/v4/verification-profiles/link/${linkId}/verification?${params.toString()}`,
        );

        if (verificationResponse.data?.id) {
            const { id } = verificationResponse.data;

            setVerificationId(id);
            params.delete('wallet');
            const query = params.toString();

            window.history.replaceState('', '', `/${id}${query ? `?${query}` : ''}`);
        }

        setWalletScreen(null);
    };

    const runWeb3Flow = async () => {
        const address = await connectWallet();

        if (!address) {
            return;
        }

        await proceedWithWallet(address);
    };

    const handleRetry = () => {
        runWeb3Flow().catch((e) => {
            // eslint-disable-next-line no-console
            console.error('Wallet retry failed', e);
        });
    };

    useEffect(() => {
        if (!isSettingsInitialized.current) {
            isSettingsInitialized.current = true;
            const getSettings = async () => {
                try {
                    const publicSettingsResponse = await defaultAxios.get(
                        '/webapi/wl/public-settings',
                    );
                    const { data } = publicSettingsResponse;

                    if (data?.logo_settings?.favicon_url) {
                        let link: HTMLLinkElement | null =
                            document.querySelector("link[rel~='icon']");

                        if (!link) {
                            link = document.createElement('link');
                            link.rel = 'icon';
                            document.querySelector('head')?.appendChild(link);
                        }
                        link.href = data.logo_settings.favicon_url;
                    }

                    if (data?.other_settings?.default_page_title) {
                        setIsDomainPageTitle(data.other_settings.default_page_title);
                    }

                    if (data) {
                        setSettings(data);
                    }

                    let newApiUrl = process.env.REACT_APP_API_BASE_URL || '';

                    if (data?.domain_settings?.api) {
                        newApiUrl = `https://${data.domain_settings.api}`;
                        setApiUrl(newApiUrl);
                    }

                    const { location } = window;

                    if (location.pathname.startsWith('/vp')) {
                        let search: URLSearchParams | null = null;

                        if (window.location.search) {
                            try {
                                search = new URLSearchParams(window.location.search);
                            } catch {
                                /* empty */
                            }
                        }

                        const linkId = location.pathname.replace('/vp/', '');

                        linkIdRef.current = linkId;
                        apiUrlRef.current = newApiUrl;
                        searchRef.current = search;

                        if (search?.get('web3')) {
                            await runWeb3Flow();

                            return;
                        }

                        const verificationResponse = await axios.get(
                            `${newApiUrl}/api/v4/verification-profiles/link/${linkId}/verification${
                                search ? `?${search.toString()}` : ''
                            }`,
                        );

                        if (verificationResponse.data?.id) {
                            const { id } = verificationResponse.data;

                            setVerificationId(id);
                            window.history.replaceState(
                                '',
                                '',
                                `/${id}${search ? `?${search.toString()}` : ''}`,
                            );
                        }
                    } else {
                        setVerificationId(location.pathname.slice(1, location.pathname.length));
                    }
                } finally {
                    setIsLoading(false);
                }
            };

            getSettings();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isLoading || walletScreen || !verificationId) {
            return;
        }

        if (!isInitialized.current) {
            isInitialized.current = true;

            if (process.env.REACT_APP_GTAG_MANAGER_ID && isDsHost) {
                TagManager.initialize({
                    gtmId: process.env.REACT_APP_GTAG_MANAGER_ID,
                });
            }

            DocsVerificationWidget({
                id: verificationId,
                elementId: 'widget',
                apiUrl,
                ...(settings ? { settings } : {}),
                ...(isDsHost
                    ? {
                          onFinish: () => {
                              TagManager.dataLayer({
                                  dataLayer: { event: 'verification_success' },
                              });
                          },
                      }
                    : {}),
            } as DocsVerificationWidgetProps);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading, verificationId, walletScreen]);

    useLogRocket(isDsHost, verificationId);

    const isMobile = isMobileDevice();
    const installHref = isMobile ? buildMetaMaskDeepLink() : METAMASK_INSTALL_URL;

    return (
        <div className='app'>
            {!!domainPageTitle && (
                <HelmetBase>
                    <title>{domainPageTitle} - DocVer Live</title>
                    <meta property='og:title' content={`${domainPageTitle} - DocVer Live`} />
                </HelmetBase>
            )}
            {isLoading && !zkProof && !walletScreen && <Spinner />}
            <div className='main-container'>
                {walletScreen && (
                    <WalletScreen
                        variant={walletScreen}
                        isMobile={isMobile}
                        isRequesting={isRequesting}
                        primaryHref={walletScreen === 'no-wallet' ? installHref : undefined}
                        onPrimary={handleRetry}
                        onSecondary={() => window.location.reload()}
                    />
                )}
                {!walletScreen && !showZkProof && <div id='widget' />}
                {!walletScreen && !isLoading && showZkProof && (
                    <React.Fragment>
                        {zkProof && (
                            <div className='zk-proof-block'>
                                <pre
                                    style={{
                                        background: '#f6f8fa',
                                        padding: 12,
                                        borderRadius: 6,
                                        fontSize: 10,
                                        height: 400,
                                        overflowY: 'auto',
                                        overflowX: 'auto',
                                        whiteSpace: 'pre',
                                    }}
                                >
                                    {JSON.stringify(zkProof, null, 2)}
                                </pre>
                            </div>
                        )}
                        {errorZkProof && <div className='error-zk-proof'>{errorZkProof}</div>}
                    </React.Fragment>
                )}
            </div>
        </div>
    );
};
